import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';

/**
 * useCreateQuote — a regra de "posso criar um orçamento agora?" num lugar só.
 *
 * Existe porque a ação passou a ter DOIS pontos de partida: o "+" da navegação
 * inferior e o CTA principal do dashboard. São quatro portões encadeados (papel
 * → cota → plano → visitante) e, duplicados, eles divergiriam na primeira
 * mudança — com o sintoma clássico: um caminho respeita o limite do plano e o
 * outro deixa passar, sem erro nenhum na tela.
 *
 * A ORDEM importa e é a mesma do resto do app: papel antes de plano (#38). Um
 * `viewer` não é bloqueado por cota — o problema dele não é assinar.
 *
 * O modal de cadastro (visitante que já gastou o orçamento grátis) fica a cargo
 * do chamador porque cada tela o monta no seu próprio lugar; o hook só diz
 * QUANDO abrir.
 */
export function useCreateQuote() {
  const { user } = useAuth();
  const { canCreateQuote, visitorQuoteUsed } = useSubscription();
  const unlimitedQuotes = useFeature('unlimited_quotes');
  const { canWrite } = useRole();
  const navigate = useNavigate();
  const [signUpOpen, setSignUpOpen] = useState(false);

  const createQuote = useCallback(() => {
    if (user) {
      // Viewers have read-only access to this company.
      if (!canWrite) {
        toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
        return;
      }
      if (canCreateQuote || unlimitedQuotes.allowed) {
        navigate('/quotes/new');
      } else {
        unlimitedQuotes.require();
      }
      return;
    }

    if (visitorQuoteUsed) {
      setSignUpOpen(true);
    } else {
      navigate('/quotes/new');
    }
  }, [user, canWrite, canCreateQuote, unlimitedQuotes, visitorQuoteUsed, navigate]);

  return { createQuote, signUpOpen, setSignUpOpen };
}
